'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: string;
  number: number;
  icon: string;
  title: string;
  description: string;
  time: string;
}

const ProcessSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const processSteps: ProcessStep[] = [
    {
      id: '1',
      number: 1,
      icon: 'PhoneIcon',
      title: 'Bezpłatna konsultacja',
      description: 'Umów się na rozmowę z naszym ekspertem. Opowiedz o swoich potrzebach finansowych i celach.',
      time: '15 minut',
    },
    {
      id: '2',
      number: 2,
      icon: 'MagnifyingGlassIcon',
      title: 'Analiza i porównanie ofert',
      description: 'Analizujemy Twoją sytuację i porównujemy oferty wszystkich partnerskich banków.',
      time: '24 godziny',
    },
    {
      id: '3',
      number: 3,
      icon: 'DocumentCheckIcon',
      title: 'Prezentacja najlepszej oferty',
      description: 'Przedstawiamy najkorzystniejszą ofertę z pełnym doradztwem i wsparciem w przygotowaniu dokumentów.',
      time: '48 godzin',
    },
  ];

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Jak to działa?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-card rounded-xl h-64 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 rounded-full mb-6">
            <Icon name="ClockIcon" size={20} variant="solid" className="text-secondary" />
            <span className="text-sm font-body font-medium text-secondary">
              Proces
            </span>
          </div>

          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Jak to działa?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Prosty, przejrzysty proces w trzech krokach - od konsultacji do najlepszej oferty w 48 godzin
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-border -translate-y-1/2" />
          <div className="hidden lg:block absolute top-1/2 left-0 h-1 bg-primary -translate-y-1/2 w-2/3" />

          <div className="grid md:grid-cols-3 gap-8 relative">
            {processSteps.map((step, index) => (
              <div key={step.id} className="relative">
                <div className="bg-card rounded-2xl shadow-card p-8 transition-all duration-250 hover:shadow-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-primary text-primary-foreground rounded-full mx-auto mb-6 relative z-10 ring-8 ring-background">
                    <span className="text-2xl font-headline font-bold">{step.number}</span>
                  </div>

                  <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-lg mx-auto mb-4">
                    <Icon name={step.icon as any} size={28} variant="outline" className="text-primary" />
                  </div>

                  <h3 className="text-xl font-headline font-bold text-foreground mb-3 text-center">
                    {step.title}
                  </h3>

                  <p className="text-sm text-muted-foreground mb-4 text-center leading-relaxed">
                    {step.description}
                  </p>

                  <div className="flex items-center justify-center gap-2 pt-4 border-t border-border">
                    <Icon name="ClockIcon" size={16} variant="outline" className="text-accent" />
                    <span className="text-sm font-body font-semibold text-accent">
                      {step.time}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-primary to-secondary rounded-2xl shadow-2xl p-8 lg:p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center w-16 h-16 bg-accent rounded-full mx-auto mb-6">
              <Icon name="BoltIcon" size={32} variant="solid" className="text-accent-foreground" />
            </div>

            <h3 className="text-2xl lg:text-3xl font-headline font-bold text-primary-foreground mb-4">
              Gwarancja 48 godzin
            </h3>

            <p className="text-lg text-primary-foreground/90 mb-8">
              Zobowiązujemy się do przedstawienia najlepszej oferty finansowania w ciągu 48 godzin od pierwszej konsultacji. To nasza gwarancja szybkości i profesjonalizmu. Nasi doradcy współpracują z Expander Advisors Sp. z o.o.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-primary-foreground/10 rounded-lg px-6 py-3">
                <p className="text-sm text-primary-foreground/80 mb-1">Bezpłatna konsultacja</p>
                <p className="text-lg font-headline font-bold text-primary-foreground">Bez zobowiązań</p>
              </div>
              <div className="bg-primary-foreground/10 rounded-lg px-6 py-3">
                <p className="text-sm text-primary-foreground/80 mb-1">Pełne wsparcie</p>
                <p className="text-lg font-headline font-bold text-primary-foreground">Na każdym etapie</p>
              </div>
              <div className="bg-primary-foreground/10 rounded-lg px-6 py-3">
                <p className="text-sm text-primary-foreground/80 mb-1">Najlepsze warunki</p>
                <p className="text-lg font-headline font-bold text-primary-foreground">Gwarantowane</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;